import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { Search, X, Banknote, ShieldCheck, CreditCard as ObAccountsLogoIcon } from 'lucide-react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

export default function OpenBankingAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fiscalFilter, setFiscalFilter] = useState('');
   const [providerFilter, setProviderFilter] = useState('');
   const [currencyFilter, setCurrencyFilter] = useState('');
   const [enabledFilter, setEnabledFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async (fiscalId) => {
    setLoading(true);
    try {
      const params = fiscalId ? { fiscal_id: fiscalId } : {};
      const res = await api.get('/open-banking/accounts', { params });
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching accounts', error);
      toast.error('Errore nel caricamento degli account bancari');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFiscalChange = (event) => {
    const value = event.target.value;
    setFiscalFilter(value);
    setPage(0);
    const term = value.trim();
    fetchData(term || undefined);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = rows.filter((row) => {
    if (providerFilter && row.provider_name !== providerFilter) return false;
    if (currencyFilter && row.currency_code !== currencyFilter) return false;
    if (enabledFilter === 'enabled' && !row.enabled) return false;
    if (enabledFilter === 'disabled' && row.enabled) return false;
    return true;
  });

  const paginated = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatCurrency = (amount, currency) => {
    if (amount == null) return '-';
    try {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount);
    } catch (e) {
      return amount;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ObAccountsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Account Bancari
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conti correnti collegati tramite provider Open Banking
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <TextField
                fullWidth
                placeholder="Filtra per CF azienda..."
                value={fiscalFilter}
                onChange={handleFiscalChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color="#637381" />
                    </InputAdornment>
                  ),
                  endAdornment: fiscalFilter ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleFiscalChange({ target: { value: '' } })}
                      >
                        <X size={18} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                label="Provider"
                value={providerFilter}
                onChange={(e) => {
                  setProviderFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                {[...new Set(rows.map((r) => r.provider_name))].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Valuta</InputLabel>
              <Select
                label="Valuta"
                value={currencyFilter}
                onChange={(e) => {
                  setCurrencyFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutte</em>
                </MenuItem>
                {[...new Set(rows.map((r) => r.currency_code))].map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Stato</InputLabel>
              <Select
                label="Stato"
                value={enabledFilter}
                onChange={(e) => {
                  setEnabledFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="enabled">Solo abilitati</MenuItem>
                <MenuItem value="disabled">Solo disabilitati</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Azienda</TableCell>
                <TableCell>CF Azienda</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>IBAN</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell>Valuta</TableCell>
                <TableCell>Abilitato</TableCell>
                <TableCell>Consenso PSD2</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessun account trovato.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row) => {
                  const name = row.name || '';
                  const looksLikeIban =
                    typeof name === 'string' &&
                    /^IT[0-9A-Z]{10,}$/i.test(name.trim());
                  const accountIdentifier =
                    row.iban ||
                    row.account_number ||
                    (looksLikeIban ? name.trim() : '') ||
                    row.account_id ||
                    row.uuid ||
                    '-';

                  return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.business_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {row.fiscal_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: '#eef2ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Banknote size={14} color="#4f46e5" />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.provider_name || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.provider_country || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {accountIdentifier}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color:
                            row.balance < 0
                              ? 'error.main'
                              : row.balance > 0
                              ? 'success.main'
                              : 'text.secondary'
                        }}
                      >
                        {formatCurrency(row.balance, row.currency_code)}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.currency_code}</TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          row.enabled ? (
                            <ShieldCheck size={14} />
                          ) : undefined
                        }
                        label={row.enabled ? 'Abilitato' : 'Disabilitato'}
                        variant="outlined"
                        color={row.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {row.consent_expires_at ? (
                        <Chip
                          label={new Date(row.consent_expires_at).toLocaleDateString('it-IT')}
                          variant="outlined"
                          color={
                            new Date(row.consent_expires_at) < new Date()
                              ? 'error'
                              : 'default'
                          }
                          size="small"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
